import { appPortalBaseQuery } from '@/features/baseQuery'
import { createApi } from '@reduxjs/toolkit/query/react'
import {
  AddFundClassRequest,
  AllVestingDetail,
  ConfirmSnapshotArgs,
  CreateVestingRuleRequest,
  DeferralDetail,
  DeferralVestingDetailsResponse,
  DownloadMonthlyReturnPdfRequest,
  EmployeeAnnualSummary,
  EmployeeAnnualSummaryRequest,
  FundMonthlyDocumentDto,
  FundMonthlyTotalDto,
  GetAllDeferralsRequest,
  GetAllFundClassesRequest,
  GetEmployeeDeferralsRequest,
  GetFundMonthlyDocumentsRequest,
  ImportDeferredBonusesRequest,
  LatestImport,
  MonthlySequenceStatus,
  UpdateVestingRuleRequest,
  UpsertMonthlyReturnsRequest,
  RenameFundClassesRequest,
  ValidateFundMonthlyTotalRequest,
  VestingRule,
  UpdateDeferralArgs,
  UndoPaymentArgs,
  UndoReversalArgs,
  UploadMonthlyReturnPdfRequest,
  UploadMonthlyReturnPdfResponse,
  PreviewBatchPaymentRequest,
  BatchPaymentRequestItem,
  BatchReversalRequestItem,
  BatchInactivateDeferralsRequest,
  BatchUpdateDeferralDetailsItem,
  UpdateDeferralVestingDetailsRequest,
  AuditTransactionSummary,
  GetAuditTransactionSummariesRequest,
  ApprovalRequestDetail,
} from './def'
import { Employee, EmployeeEnums } from '../employees/types'
import { FundClassWithMonthlyReturn } from '../funds/types'
import { BatchPaymentPreview, DeferredBonus } from '../deferrals/types'

const DEFERRED_BONUS_BASE_URL = 'api/deferred-bonus'

export const deferredBonusesApi = createApi({
  reducerPath: 'deferredBonusesApi',
  baseQuery: appPortalBaseQuery({
    baseUrl: import.meta.env.VITE_FINANCE_PORTAL_API_BASE_URL,
  }),
  tagTypes: ['DeferredBonuses', 'FundClasses', 'LatestImport', 'Funds', 'VestingRules', 'VestingDetails', 'FundMonthlyDocuments', 'AuditTransactionSummaries', 'MonthlySequenceStatus', 'ApprovalRequests'],
  endpoints: (builder) => ({
    importDeferredBonuses: builder.mutation<void, ImportDeferredBonusesRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('ExcelFile', request.excelFile)
        formData.append('Mode', String(request.mode === 'Append' ? 1 : 0))

        return {
          url: `${DEFERRED_BONUS_BASE_URL}/import-excel`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['DeferredBonuses', 'LatestImport', 'MonthlySequenceStatus'],
    }),
    getEmployeeDeferrals: builder.query<DeferredBonus[], GetEmployeeDeferralsRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-employee-deferrals`,
        method: 'POST',
        body: request,
      }),
      providesTags: ['DeferredBonuses'],
    }),
    getAllEmployees: builder.query<Employee[], void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-all-employees`,
        method: 'GET',
      }),
    }),
    getEmployeeEnums: builder.query<EmployeeEnums, void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-employee-enums`,
        method: 'GET',
      }),
    }),
    getAllFundClasses: builder.query<FundClassWithMonthlyReturn[], GetAllFundClassesRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-all-fund-classes`,
        method: 'GET',
        params: {
          fiscalYear: request.fiscalYear,
        },
      }),
      providesTags: ['FundClasses'],
    }),
    upsertMonthlyReturns: builder.mutation<void, UpsertMonthlyReturnsRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/upsert-monthly-returns`,
        method: 'POST',
        body: request,
      }),
      // 'FundClasses' is deliberately not invalidated here: the Funds page refetches it itself once every
      // call in a Save has succeeded, so a partial failure doesn't wipe the grid's unsaved edits.
      invalidatesTags: ['DeferredBonuses', 'MonthlySequenceStatus'],
    }),
    renameFundClasses: builder.mutation<number, RenameFundClassesRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/rename-fund-classes`,
        method: 'POST',
        body: request,
      }),
      // Fund class names also show on the deferral and vesting grids. 'FundClasses' is refetched by the
      // Funds page, as for upsertMonthlyReturns.
      invalidatesTags: ['DeferredBonuses', 'VestingDetails'],
    }),
    getDeferral: builder.query<DeferralDetail, string>({
      query: (deferralId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-deferral/${deferralId}`,
        method: 'GET',
      }),
    }),
    getEmployeeAnnualSummary: builder.query<EmployeeAnnualSummary[], EmployeeAnnualSummaryRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-employee-annual-summary`,
        method: 'GET',
        params: {
          fiscalYear: request.fiscalYear,
          asOfMonth: request.asOfMonth ?? undefined,
        },
      }),
      providesTags: ['DeferredBonuses'],
    }),
    getAllDeferrals: builder.query<DeferralDetail[], GetAllDeferralsRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-all-deferrals`,
        method: 'GET',
        params: {
          fiscalYear: request.fiscalYear,
          asOfMonth: request.asOfMonth ?? undefined,
        },
      }),
      providesTags: ['DeferredBonuses'],
    }),
    getLatestImport: builder.query<LatestImport, void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-latest-import`,
        method: 'GET',
      }),
      providesTags: ['LatestImport'],
    }),
    getMonthlySequenceStatus: builder.query<MonthlySequenceStatus, void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/monthly-sequence-status`,
        method: 'GET',
      }),
      providesTags: ['MonthlySequenceStatus'],
    }),
    getAllFunds: builder.query<string[], void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-all-funds`,
        method: 'GET',
      }),
      providesTags: ['Funds'],
    }),
    addFundClass: builder.mutation<void, AddFundClassRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/add-fund-class`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['FundClasses', 'Funds'],
    }),
    deleteFundClass: builder.mutation<void, string>({
      query: (fundClassId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/fund-class/${fundClassId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FundClasses'],
    }),
    getAllVestingRules: builder.query<VestingRule[], void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-all-vesting-rules`,
        method: 'GET',
      }),
      providesTags: ['VestingRules'],
    }),
    createVestingRule: builder.mutation<string, CreateVestingRuleRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/vesting-rules/create`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['VestingRules'],
    }),
    updateVestingRule: builder.mutation<void, UpdateVestingRuleRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/vesting-rules/update`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['VestingRules'],
    }),
    deleteVestingRule: builder.mutation<void, string>({
      query: (id) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/vesting-rules/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['VestingRules'],
    }),
    getDeferralVestingDetails: builder.query<DeferralVestingDetailsResponse, string>({
      query: (deferralId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-deferral-vesting-details/${deferralId}`,
        method: 'GET',
      }),
      providesTags: ['VestingDetails'],
    }),
    getAllVestingDetails: builder.query<AllVestingDetail[], void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-all-vesting-details`,
        method: 'GET',
      }),
      providesTags: ['VestingDetails'],
    }),
    updateDeferral: builder.mutation<string, UpdateDeferralArgs>({
      query: ({ deferralId, body }) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/update-deferrals/${deferralId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['DeferredBonuses'],
    }),
    // Undoing a payment or reversal is no longer applied directly: these raise an approval request (202 with
    // the request id) and nothing changes on the deferral until a checker approves it on the Change Requests
    // page. Hence they invalidate the request list, not the deferral data.
    requestUndoPayment: builder.mutation<string, UndoPaymentArgs>({
      query: ({ paymentId, body }) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/undo-payment/${paymentId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ApprovalRequests'],
    }),
    requestUndoReversal: builder.mutation<string, UndoReversalArgs>({
      query: ({ reversalId, body }) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/undo-reversal/${reversalId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ApprovalRequests'],
    }),
    // Re-activating an inactive deferral is the only way to make it editable again for payments and
    // reversals; approval flips IsActive and nothing else. No body: the deferral id is the whole request.
    requestReopenInactiveDeferral: builder.mutation<string, string>({
      query: (deferralId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/reopen-deferral/${deferralId}`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalRequests'],
    }),
    // Raises a request to permanently delete a never-snapshotted deferral. Nothing changes until a
    // checker approves, so only the approval queue is invalidated; approval itself refreshes the grids.
    // No body: the deferral id is the whole request. 409 when the deferral is already snapshotted.
    requestDeleteDeferral: builder.mutation<string, string>({
      query: (deferralId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/delete-deferral/${deferralId}`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalRequests'],
    }),
    getFundMonthlyDocuments: builder.query<FundMonthlyDocumentDto[], GetFundMonthlyDocumentsRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/fund-monthly-documents`,
        method: 'POST',
        body: request,
      }),
      providesTags: ['FundMonthlyDocuments'],
    }),
    uploadMonthlyReturnPdf: builder.mutation<UploadMonthlyReturnPdfResponse, UploadMonthlyReturnPdfRequest>({
      query: ({ fundId, year, month, pdfFile }) => {
        const formData = new FormData()
        formData.append('FundId', fundId)
        formData.append('Year', String(year))
        formData.append('Month', String(month))
        formData.append('PdfFile', pdfFile, pdfFile.name)
        return {
          url: `${DEFERRED_BONUS_BASE_URL}/upload-monthly-return-pdf`,
          method: 'POST',
          body: formData,
        }
      },
      // 'FundClasses' carries the per-month PDF flags shown as ticks on the funds grid.
      invalidatesTags: ['FundMonthlyDocuments', 'FundClasses'],
    }),
    // The server always serves this from the deferred-bonus bucket, so no bucket name is sent.
    downloadMonthlyReturnPdf: builder.mutation<Blob, DownloadMonthlyReturnPdfRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/download-monthly-return-pdf`,
        method: 'POST',
        body: request,
        responseHandler: (response) => response.blob(),
      }),
    }),
    validateFundMonthlyTotal: builder.mutation<FundMonthlyTotalDto, ValidateFundMonthlyTotalRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/validate-fund-monthly-total`,
        method: 'POST',
        body: request,
      }),
    }),
    confirmSnapshot: builder.mutation<void, ConfirmSnapshotArgs>({
      query: ({ year, month, items }) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/confirm-snapshot`,
        method: 'POST',
        params: { year, month },
        body: items,
      }),
      invalidatesTags: ['DeferredBonuses', 'MonthlySequenceStatus'],
    }),
    previewBatchPayment: builder.query<BatchPaymentPreview[], PreviewBatchPaymentRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/batch-payment/preview`,
        method: 'POST',
        body: request,
      }),
      providesTags: ['DeferredBonuses'],
    }),
    batchPayment: builder.mutation<string[], BatchPaymentRequestItem[]>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/batch-payment`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['DeferredBonuses'],
    }),
    batchReversal: builder.mutation<string[], BatchReversalRequestItem[]>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/batch-reversal`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['DeferredBonuses'],
    }),
    batchInactivateDeferrals: builder.mutation<string[], BatchInactivateDeferralsRequest>({
      query: (deferralIds) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/batch-inactivate`,
        method: 'POST',
        body: deferralIds,
      }),
      invalidatesTags: ['DeferredBonuses'],
    }),
    batchUpdateDeferralDetails: builder.mutation<string[], BatchUpdateDeferralDetailsItem[]>({
      query: (items) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/batch-update-deferral-details`,
        method: 'PUT',
        body: items,
      }),
      invalidatesTags: ['DeferredBonuses'],
    }),
    updateDeferralVestingDetails: builder.mutation<void, UpdateDeferralVestingDetailsRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/update-deferral-vesting-details`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['VestingDetails'],
    }),
    updateEmployees: builder.mutation<void, Employee[]>({
      query: (employees) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/update-employees`,
        method: 'PUT',
        body: employees,
      }),
    }),
    getAuditTransactionSummaries: builder.query<AuditTransactionSummary[], GetAuditTransactionSummariesRequest>({
      query: (request) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/get-audit-transaction-summaries`,
        method: 'GET',
        params: {
          year: request.year,
          month: request.month,
        },
      }),
      providesTags: ['AuditTransactionSummaries'],
    }),

    // ── Approval requests (maker-checker) ───────────────────────────────────
    // The list carries every request in every status plus the affected deferral and the effect of
    // approving, so the page needs no other call; filtering by status is done in the grid.
    getApprovalRequests: builder.query<ApprovalRequestDetail[], void>({
      query: () => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests`,
        method: 'GET',
      }),
      providesTags: ['ApprovalRequests'],
    }),
    // Approving runs the requested operation, so the deferral data changes too.
    approveApprovalRequest: builder.mutation<string, string>({
      query: (requestId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/${requestId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalRequests', 'DeferredBonuses'],
    }),
    rejectApprovalRequest: builder.mutation<string, string>({
      query: (requestId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/${requestId}/reject`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalRequests'],
    }),
    cancelApprovalRequest: builder.mutation<string, string>({
      query: (requestId) => ({
        url: `${DEFERRED_BONUS_BASE_URL}/approval-requests/${requestId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalRequests'],
    }),
  }),
})

export const {
  useImportDeferredBonusesMutation,
  useGetEmployeeDeferralsQuery,
  useGetAllDeferralsQuery,
  useGetAllEmployeesQuery,
  useGetEmployeeEnumsQuery,
  useGetAllFundClassesQuery,
  useUpsertMonthlyReturnsMutation,
  useRenameFundClassesMutation,
  useGetDeferralQuery,
  useGetEmployeeAnnualSummaryQuery,
  useGetLatestImportQuery,
  useGetMonthlySequenceStatusQuery,
  useGetAllFundsQuery,
  useAddFundClassMutation,
  useDeleteFundClassMutation,
  useGetAllVestingRulesQuery,
  useCreateVestingRuleMutation,
  useUpdateVestingRuleMutation,
  useDeleteVestingRuleMutation,
  useGetDeferralVestingDetailsQuery,
  useGetAllVestingDetailsQuery,
  useUpdateDeferralMutation,
  useRequestUndoPaymentMutation,
  useRequestUndoReversalMutation,
  useRequestReopenInactiveDeferralMutation,
  useRequestDeleteDeferralMutation,
  useGetFundMonthlyDocumentsQuery,
  useUploadMonthlyReturnPdfMutation,
  useDownloadMonthlyReturnPdfMutation,
  useValidateFundMonthlyTotalMutation,
  useConfirmSnapshotMutation,
  usePreviewBatchPaymentQuery,
  useBatchPaymentMutation,
  useBatchReversalMutation,
  useBatchInactivateDeferralsMutation,
  useBatchUpdateDeferralDetailsMutation,
  useUpdateDeferralVestingDetailsMutation,
  useUpdateEmployeesMutation,
  useGetAuditTransactionSummariesQuery,
  useGetApprovalRequestsQuery,
  useApproveApprovalRequestMutation,
  useRejectApprovalRequestMutation,
  useCancelApprovalRequestMutation,
} = deferredBonusesApi
